import { useRef, useState } from 'react';
import './HealthCheckMain.css';

const DIMENSIONS = [
  { id: 'morale', label: 'Team Morale', description: 'How are you feeling about being part of this team?' },
  { id: 'delivery', label: 'Delivery Speed', description: 'How happy are you with our pace of delivering value?' },
  { id: 'quality', label: 'Code Quality', description: 'How proud are you of the quality of what we ship?' },
  { id: 'learning', label: 'Learning & Growth', description: 'Are you learning and growing professionally?' },
  { id: 'collaboration', label: 'Collaboration', description: 'How well do we work together as a team?' },
  { id: 'fun', label: 'Fun', description: 'Are you having fun at work?' },
  { id: 'direction', label: 'Direction & Goals', description: 'Do you understand and believe in what we\'re building?' },
  { id: 'support', label: 'Support & Process', description: 'Do you have the tools and support you need?' },
];

const RATINGS = [
  { value: 'green',  emoji: '🔥', label: 'On Fire!',    bg: '#d1fae5', border: '#10b981', text: '#065f46' },
  { value: 'yellow', emoji: '😐', label: 'Meh',         bg: '#fef3c7', border: '#f59e0b', text: '#78350f' },
  { value: 'red',    emoji: '💩', label: 'It\'s a 💩',  bg: '#fee2e2', border: '#ef4444', text: '#7f1d1d' },
];

function dominantRating(counts) {
  const total = counts.green + counts.yellow + counts.red;
  if (total === 0) return null;
  if (counts.red / total > 0.5) return 'red';
  if (counts.green / total > 0.5) return 'green';
  return 'yellow';
}

export default function HealthCheckMain({ sessionId, authorName, cards }) {
  const [voting, setVoting] = useState({});
  const [commentText, setCommentText] = useState({});
  const [submittingComment, setSubmittingComment] = useState({});
  const inputRefs = useRef({});

  // Separate votes from comments
  const voteMap = {};
  const commentMap = {};
  for (const card of cards) {
    if (card.category.endsWith('-comment')) {
      const dimId = card.category.slice(0, -'-comment'.length);
      if (!commentMap[dimId]) commentMap[dimId] = [];
      commentMap[dimId].push(card);
    } else {
      if (!voteMap[card.category]) voteMap[card.category] = {};
      voteMap[card.category][card.author] = card.content;
    }
  }

  async function vote(dimensionId, rating) {
    if (!authorName || voting[dimensionId]) return;
    setVoting(v => ({ ...v, [dimensionId]: true }));
    try {
      await fetch(`/api/sessions/${sessionId}/cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ author: authorName, category: dimensionId, content: rating }),
      });
    } finally {
      setVoting(v => ({ ...v, [dimensionId]: false }));
    }
  }

  async function submitComment(e, dimensionId) {
    e.preventDefault();
    const text = commentText[dimensionId]?.trim();
    if (!text || !authorName || submittingComment[dimensionId]) return;
    setSubmittingComment(s => ({ ...s, [dimensionId]: true }));
    try {
      await fetch(`/api/sessions/${sessionId}/cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ author: authorName, category: `${dimensionId}-comment`, content: text }),
      });
      setCommentText(t => ({ ...t, [dimensionId]: '' }));
      inputRefs.current[dimensionId]?.focus();
    } finally {
      setSubmittingComment(s => ({ ...s, [dimensionId]: false }));
    }
  }

  return (
    <div className="hc-grid">
      {DIMENSIONS.map(({ id, label, description }) => {
        const dimVotes = voteMap[id] ?? {};
        const myVote = dimVotes[authorName];
        const counts = { green: 0, yellow: 0, red: 0 };
        for (const v of Object.values(dimVotes)) {
          if (counts[v] !== undefined) counts[v]++;
        }
        const dominant = dominantRating(counts);
        const totalVotes = counts.green + counts.yellow + counts.red;
        const comments = commentMap[id] ?? [];

        return (
          <div key={id} className={`hc-card ${dominant ? `hc-card--${dominant}` : ''}`}>
            <div className="hc-card-header">
              <span className="hc-card-label">{label}</span>
              <span className="hc-card-desc">{description}</span>
            </div>

            <div className="hc-vote-row">
              {RATINGS.map(({ value, emoji, label: rLabel, bg, border, text }) => (
                <button
                  key={value}
                  className={`hc-vote-btn ${myVote === value ? 'hc-vote-btn--active' : ''}`}
                  style={myVote === value ? { background: bg, borderColor: border, color: text } : {}}
                  onClick={() => vote(id, value)}
                  disabled={!authorName || voting[id]}
                  title={authorName ? rLabel : 'Join the session to vote'}
                >
                  <span className="hc-vote-emoji">{emoji}</span>
                  <span className="hc-vote-label">{rLabel}</span>
                </button>
              ))}
            </div>

            <div className="hc-tally">
              {totalVotes > 0 ? (
                RATINGS.map(({ value, emoji }) => counts[value] > 0 && (
                  <span key={value} className="hc-tally-item">
                    {emoji} {counts[value]}
                  </span>
                ))
              ) : (
                <span className="hc-tally-empty">No votes yet</span>
              )}
            </div>

            {(comments.length > 0 || authorName) && (
              <div className="hc-comments">
                {comments.map((c) => (
                  <div key={c.id} className="hc-comment">
                    <span className="hc-comment-author">{c.author}</span>
                    <span className="hc-comment-text">{c.content}</span>
                  </div>
                ))}
                {authorName && (
                  <form className="hc-comment-form" onSubmit={(e) => submitComment(e, id)}>
                    <input
                      ref={(el) => { inputRefs.current[id] = el; }}
                      className="hc-comment-input"
                      type="text"
                      placeholder="Add a comment…"
                      value={commentText[id] ?? ''}
                      onChange={(e) => setCommentText(t => ({ ...t, [id]: e.target.value }))}
                      maxLength={500}
                    />
                    <button
                      className="hc-comment-btn"
                      type="submit"
                      disabled={!commentText[id]?.trim() || submittingComment[id]}
                    >
                      Add
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
